import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/api/client";
import type {
  EntityMeta,
  EntityRecord,
  EntitySummary,
  HistoryEntry,
  ListParams,
  ListResponse,
  RelatedMeta,
  LookupItem,
  SectionMeta,
  ValidationIssue,
} from "./types";

/* ------------------------------------------------------------ query keys */
export const entityKeys = {
  metaAll: ["meta", "entities"] as const,
  sections: ["meta", "sections"] as const,
  meta: (name: string) => ["meta", "entity", name] as const,
  entity: (name: string) => ["entities", name] as const,
  list: (name: string, params: ListParams) => ["entities", name, "list", params] as const,
  record: (name: string, id: string) => ["entities", name, "record", id] as const,
  history: (name: string, id: string) => ["entities", name, "history", id] as const,
  lookup: (entity: string) => ["lookup", entity] as const,
};

const META_STALE = 5 * 60_000;

/* ----------------------------------------------------------------- errors */

/** 409 from PATCH: someone else saved first. `current` is the server's latest version. */
export class ConflictError extends Error {
  readonly current: EntityRecord;
  constructor(message: string, current: EntityRecord) {
    super(message);
    this.name = "ConflictError";
    this.current = current;
  }
}

export function errorStatus(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined;
}

/** Field-level issues from a 400/422 `{"detail": [{loc, msg}]}` response. */
export function validationIssues(error: unknown): ValidationIssue[] {
  if (!(error instanceof ApiError)) return [];
  const body = error.body as { detail?: unknown } | null;
  if (!body || !Array.isArray(body.detail)) return [];
  return body.detail.filter(
    (d): d is ValidationIssue => !!d && typeof d === "object" && "msg" in d && Array.isArray((d as ValidationIssue).loc),
  );
}

/* --------------------------------------------------------------- helpers */
const enc = encodeURIComponent;

/** Build the list query string. Repeats `f.<field>` for "any of" filters. */
export function buildListQuery(params: ListParams): string {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));
  if (params.sort) qs.set("sort", params.sort);
  if (params.search) qs.set("search", params.search);
  if (params.archived && params.archived !== "false") qs.set("archived", params.archived);
  if (params.columns?.length) qs.set("columns", params.columns.join(","));
  for (const [key, value] of Object.entries(params.filters ?? {})) {
    if (value === undefined) continue;
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) if (v !== "") qs.append(`f.${key}`, v);
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * After a save: the record's own lists and lookups, and every other open view, because a save can change other
 * records too (a milestone's date moves its financials, a lead's status adds to its timeline, a ticket raises
 * notifications). Only views on screen refetch; table definitions (`meta`) are left alone.
 */
function invalidateEntity(qc: QueryClient, name: string) {
  void qc.invalidateQueries({ queryKey: entityKeys.entity(name) });
  void qc.invalidateQueries({ queryKey: entityKeys.lookup(name) });
  void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "meta" && q.queryKey[0] !== "entities" });
  void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "entities" && q.queryKey[1] !== name });
}

function storeRecord(qc: QueryClient, name: string, item: EntityRecord) {
  qc.setQueryData(entityKeys.record(name, item.id), item);
}

/* ------------------------------------------------------------- metadata */
export function useEntitiesMeta() {
  return useQuery({
    queryKey: entityKeys.metaAll,
    queryFn: ({ signal }) => apiFetch<EntitySummary[]>("/meta/entities", { signal }),
    staleTime: META_STALE,
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

export function useSectionsMeta() {
  return useQuery({
    queryKey: entityKeys.sections,
    queryFn: ({ signal }) => apiFetch<SectionMeta[]>("/meta/sections", { signal }),
    staleTime: META_STALE,
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

export function useEntityMeta(name: string | undefined) {
  return useQuery({
    queryKey: entityKeys.meta(name ?? ""),
    queryFn: ({ signal }) => apiFetch<EntityMeta>(`/meta/entities/${enc(name ?? "")}`, { signal }),
    staleTime: META_STALE,
    enabled: !!name,
  });
}

/** Several tables' metadata at once (shares the cache with useEntityMeta), keyed by name; missing until loaded. */
export function useEntityMetas(names: string[]): Record<string, EntityMeta | undefined> {
  return useQueries({
    queries: names.map((name) => ({
      queryKey: entityKeys.meta(name),
      queryFn: ({ signal }: { signal: AbortSignal }) => apiFetch<EntityMeta>(`/meta/entities/${enc(name)}`, { signal }),
      staleTime: META_STALE,
    })),
    combine: (results) => Object.fromEntries(names.map((name, i) => [name, results[i]?.data])),
  });
}

/* --------------------------------------------------------------- records */
export function useEntityList(name: string, params: ListParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: entityKeys.list(name, params),
    queryFn: ({ signal }) => apiFetch<ListResponse>(`/entities/${enc(name)}${buildListQuery(params)}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** Cheap row counts for related tabs (page_size=1, read `total`). Index-aligned with `related`. */
export function useRelatedCounts(related: RelatedMeta[], parentId: string | undefined): (number | undefined)[] {
  return useQueries({
    queries: related.map((r) => {
      const params: ListParams = { page: 1, page_size: 1, filters: { [r.field]: parentId ?? "" } };
      return {
        queryKey: entityKeys.list(r.entity, params),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiFetch<ListResponse>(`/entities/${enc(r.entity)}${buildListQuery(params)}`, { signal }),
        enabled: !!parentId,
        staleTime: 30_000,
      };
    }),
    combine: (results) => results.map((q) => q.data?.total),
  });
}

export function useRecord(name: string, id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: entityKeys.record(name, id),
    queryFn: ({ signal }) => apiFetch<EntityRecord>(`/entities/${enc(name)}/${enc(id)}`, { signal }),
    enabled: options.enabled ?? true,
  });
}

export function useHistory(name: string, id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: entityKeys.history(name, id),
    queryFn: ({ signal }) => apiFetch<HistoryEntry[]>(`/entities/${enc(name)}/${enc(id)}/history`, { signal }),
    enabled: options.enabled ?? true,
  });
}

export function useCreateRecord(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<EntityRecord>(`/entities/${enc(name)}`, { method: "POST", body }),
    onSuccess: (item) => {
      storeRecord(qc, name, item);
      invalidateEntity(qc, name);
    },
  });
}

export interface UpdateVariables {
  id: string;
  rowVersion: string;
  body: Record<string, unknown>;
}

/** PATCH with optimistic concurrency. A 409 rejects with ConflictError carrying `current`. */
export function useUpdateRecord(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, rowVersion, body }: UpdateVariables) => {
      try {
        return await apiFetch<EntityRecord>(`/entities/${enc(name)}/${enc(id)}`, {
          method: "PATCH",
          headers: { "If-Match": rowVersion },
          body,
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          const current = (error.body as { current?: EntityRecord } | null)?.current;
          if (current) {
            storeRecord(qc, name, current);
            throw new ConflictError(error.message, current);
          }
        }
        throw error;
      }
    },
    onSuccess: (item) => {
      storeRecord(qc, name, item);
      invalidateEntity(qc, name);
    },
  });
}

function useLifecycle(name: string, action: "archive" | "restore") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<EntityRecord>(`/entities/${enc(name)}/${enc(id)}/${action}`, { method: "POST" }),
    onSuccess: (item) => {
      storeRecord(qc, name, item);
      invalidateEntity(qc, name);
    },
  });
}

export const useArchive = (name: string) => useLifecycle(name, "archive");
export const useRestore = (name: string) => useLifecycle(name, "restore");

/** Delete for good (administrators only; `meta.can_delete`). Everyone else archives. */
export function useDeleteRecord(name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/entities/${enc(name)}/${enc(id)}`, { method: "DELETE" }),
    onSuccess: () => invalidateEntity(qc, name),
  });
}

/* --------------------------------------------------------------- lookups */
export function useLookup(
  entity: string,
  search: string,
  listKey?: string | null,
  options: { enabled?: boolean; where?: string | null } = {},
) {
  return useQuery({
    queryKey: [...entityKeys.lookup(entity), listKey ?? null, "search", search, options.where ?? null] as const,
    queryFn: ({ signal }) =>
      apiFetch<LookupItem[]>(`/lookup/${enc(entity)}`, {
        signal,
        query: { search: search || undefined, limit: 20, list_key: listKey ?? undefined, where: options.where ?? undefined },
      }),
    // Keep the old options while typing, but not when the restriction changed (another project's milestones).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey.at(-1) === (options.where ?? null) ? keepPreviousData(previous) : undefined,
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

/** Resolve labels for known ids (e.g. a prefilled or URL-filtered lookup value). */
export function useLookupLabels(entity: string | undefined, ids: string[], listKey?: string | null) {
  const wanted = ids.filter(Boolean);
  return useQuery({
    queryKey: [...entityKeys.lookup(entity ?? ""), listKey ?? null, "ids", wanted.join(",")] as const,
    queryFn: ({ signal }) =>
      apiFetch<LookupItem[]>(`/lookup/${enc(entity ?? "")}`, {
        signal,
        query: { ids: wanted.join(","), list_key: listKey ?? undefined },
      }),
    enabled: !!entity && wanted.length > 0,
    staleTime: 5 * 60_000,
  });
}
